<script setup>
import { ref, computed } from "vue";
import { modal } from '@shop/frontend/web/js/components/Modal';
import ITeams from "@components/ITeams/ITeams.vue";
import ITicket from "@components/ITicket/ITicket.vue";
import ILink from "@components/ILink/ILink.vue";
import IPackageEvent from '@components/IPackageEvent/IPackageEvent.vue';
import IPackageEventName from '@components/IPackageEventName/IPackageEventName.vue';
import ExtraInfo from "@components/ExtraInfo.vue";
import { dateHelper } from "@shop/frontend/web/js/helpers/dateHelper";
import useCartStore from "@vueApp/store/modules/cart";
import { translator } from "@shop/frontend/web/js/services/TranslatorService";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/ru';

dayjs.extend(utc);
dayjs.locale('ru');

translator.getLanguages();

const cartStore = useCartStore();
const props = defineProps({
	itemCase: {
		type: Object,
		required: true,
	},
	trashButton: {
		type: Boolean,
		default: true,
	},
	isHistory: {
		type: Boolean,
		default: false,
	},
	showTeams: {
		type: Boolean,
		default: true,
	},
});

const isEventsListOpen = ref(true);
const emits = defineEmits(["removeEvent"]);

const eventFormattedDate = ref(
	dayjs(props.itemCase.startTimestampStr).format('DD MMMM, dddd HH:mm')
);

const isCertificate = computed(() => {
	return props.itemCase.type === 'certificate' ? 'certificate' : '';
})

function removeTicket(ticket, eventId, type) {
  const adultTicketsCount = props.itemCase.tickets.reduce(
    (count, currentTicket) =>
      currentTicket.loyaltyCategoryId === null ? count + 1 : count,
    0
  );

  const childTicketsCount = props.itemCase.tickets.reduce(
    (count, currentTicket) =>
      currentTicket.loyaltyCategoryId === 1 ? count + 1 : count,
    0
  );

  const isAdult = ticket.loyaltyCategoryId === null;
  const isChild = ticket.loyaltyCategoryId === 1;

  const adultAfter = adultTicketsCount - (isAdult ? 1 : 0);
  const childAfter = childTicketsCount - (isChild ? 1 : 0);

  if (adultAfter === 0 && childAfter > 0) {
    modal.error(translator.t('order', 'Need at least one adult ticket!'));
    return;
  }

  if (type === 'service_simple') {
    cartStore.removeServiceTicket(ticket, eventId);
  } else {
    cartStore.removeEventTicket(ticket, eventId);
  }
}


function removeEvent(eventId, type, countTickets) {
	if (type === "service_simple") {
		cartStore.removeService(eventId, props.itemCase.orderId, countTickets);
	} else {
		cartStore.removeEvent(eventId, props.itemCase.orderId);
	}
}

function changeLoyalty(ticket, eventId, categoryId, priceId) {
	cartStore.changeLoyalty(ticket, eventId, props.itemCase.orderId, categoryId, priceId);
}
</script>

<template>
	<div class="i-order-event-item" :class="{ 'i-order-event-item_history': isHistory }">

		<div class="i-order-event-item__top-left">
			<a class="i-order-event-item__team-link" :href="itemCase.pageLink">
				<template v-if="itemCase.type == 'vagrant'">
					<div v-if="showTeams" class="i-order-event-item__name">
						<IPackageEventName v-if="itemCase.eventType === 'Пакет' && itemCase.participants?.team"
							:package-event="itemCase" />
						<ITeams v-else-if="itemCase.participants?.team" 
							:first-team="itemCase.participants.team.homeTeam"
							:second-team="itemCase.participants.team.guestTeam"
							:name="itemCase.name"
							:is-home-logo-with-opponent="itemCase.isHomeLogoWithOpponent" 
							:is-small="isHistory"
							class-name="i-teams_order" 
						/>
						<div v-else class="name">
							<div v-if="itemCase.eventType === 'Абонемент'" class="name__img">
								<img :src="itemCase.participants?.singular.imageUrl" alt="eventImage" />
							</div>
							<div class="name__title">
								{{ itemCase.participants?.singular.name }}
							</div>
						</div>
					</div>
					<div v-if="itemCase.eventType !== 'Пакет'" class="i-order-event-item__details">
						<span class="time">
							<span>{{ eventFormattedDate }}</span>
						</span>
						<span class="place">
							<svg>
								<use xlink:href="#location"></use>
							</svg>
							{{ itemCase.unitName }}
						</span>
					</div>
				</template>
				<template v-else-if="showTeams">
					<div class="i-order-event-item__name"
						:class="{ 'i-order-event-item__name_service': itemCase.type === 'service_simple' }">
						<div class="name">
							<div class="name__title" data-test-id="history_item_inner_name">{{ itemCase.name }}</div>
						</div>
					</div>
				</template>
			</a>
			<button v-if="itemCase.eventType === 'Пакет' && !isHistory" class="i-order-event-item__btn-events-open" @click="isEventsListOpen = !isEventsListOpen">
				<svg class="i-order-event-item__arrow-svg"
					:class="{ 'i-order-event-item__arrow-svg_active': isEventsListOpen }">
					<use xlink:href="#down"></use>
				</svg>
			</button>
		</div>
		<div class="i-order-event-item__top-right">
			<div v-if="!isHistory" class="i-order-event-item__summary-price">
				{{ translator.t('main', 'Price') }}: <span>{{ itemCase.summaryPrice }} ₽</span>
			</div>
			<ILink v-if="trashButton" :icon-name="'trash'" class="desktop trash-btn" data-test-id="remove_order_btn" @click="
				removeEvent(
					itemCase.id,
					type ?? itemCase.type,
					ticketsCount ?? itemCase.tickets.length
				)
				">
				{{ translator.t('main', 'Remove') }}
			</ILink>
			<ILink v-if="trashButton" :icon-name="'close'" class="mobile close-btn" data-test-id="remove_order_mobile_btn"
				@click="removeEvent(itemCase.id)" />
		</div>
		<div 
			v-if="itemCase?.packageEvents?.length && isEventsListOpen"
			class="i-order-event-item__package-events"
		>
			<IPackageEvent 
				v-for="packageEvent in itemCase.packageEvents" 
				:key="packageEvent.startTimestamp"
				:package-event="packageEvent"
				:is-history="isHistory"
			/>
		</div>
		<div class="i-order-event-item__tickets">
			<slot>
				<div v-for="ticket in itemCase.tickets" :key="ticket.place" class="tickets-item">
					<ITicket
						:items-case="itemCase" 
						:item-order-id="itemCase.orderId" 
						:item-parent-id="ticket.uid"
						:ticket="ticket" 
						:is-history="isHistory" 
						:inactive="false" 
						:short="false"
						:allowed-loyalty="itemCase.allowedLoyaltyCategories" 
						@remove-ticket="curTicket => removeTicket(curTicket, itemCase.id, itemCase.type)" 
						@change-loyalty="
							({ ticket, categoryId, priceId }) =>
							changeLoyalty(ticket, itemCase.id, categoryId, priceId)
						" 				
						:type="isCertificate"
					>
						<ExtraInfo 
							v-if="ticket.isEnableExtraInfo" 
							:key="ticket.uid" 
							:ticket="ticket"
							:is-history="isHistory"
						/>
					</ITicket>
				</div>
			</slot>
		</div>
	</div>
</template>

<style scoped lang="scss">
@import "frontend/web/src/styles/utils/variables.scss";
@import "./i-order-event-item.scss";

.fade-enter-active,
.fade-leave-active {
	transition: height 0.5s ease-in-out;
}

.fade-enter-to,
.fade-leave-from {
	overflow: hidden;
	height: 100%;
}

.fade-enter-from,
.fade-leave-to {
	overflow: hidden;
	height: 0;
}
</style>
